import { MutableRefObject, useEffect, useRef, useState, useCallback } from 'react'
import { AtAnchorLinks, AtAnchorLinksProps, AtButton, AtButtonProps } from '../../atoms'

type OrSecondaryNavbarBdpProps = Omit<AtAnchorLinksProps, 'onClick' | 'visibleKey'> & {
  button?: AtButtonProps
  observerRefs: MutableRefObject<{
    [key: string]: Element | null
  }>
}

/** Links for sections inside the Building Detail Page, **visible only on desktop** */
export const OrSecondaryNavbarBdp = ({ button, links, accountForNavbar, observerRefs }: OrSecondaryNavbarBdpProps) => {
  const [visibleKey, setVisibleKey] = useState('')
  const scrollingToKey = useRef<string | null>(null)
  const observers: MutableRefObject<{
    [key: string]: IntersectionObserver
  }> = useRef({})

  const onClick = (key: string) => {
    scrollingToKey.current = key
    setVisibleKey(key)
  }

  const observerCallback = useCallback(
    (e: IntersectionObserverEntry[], key: string) => {
      if (e.length && e[0].isIntersecting) {
        if (scrollingToKey.current === key) {
          scrollingToKey.current = null
          setVisibleKey(key)
        } else if (scrollingToKey.current === null) {
          setVisibleKey(key)
        }
      }
    },
    [scrollingToKey]
  )

  useEffect(() => {
    const observersCopy = observers.current
    for (const key in observerRefs.current) {
      const observer = observerRefs.current[key]

      if (observer !== null) {
        observers.current[key] = new IntersectionObserver((e) => observerCallback(e, key), {
          rootMargin: '0px',
          threshold: 0.6,
        })
        observers.current[key].observe(observer)
      }
    }

    return () => {
      for (const key in observersCopy) {
        observersCopy[key].disconnect()
      }
    }
  }, [observerRefs, observers])

  return (
    <div
      id="secondary-navigation"
      className="hidden px-4 md:px-30 lg:px-0 lg:mx-auto lg:flex w-full h-[5.9375rem] gap-16 items-center bg-neutrals-white sticky top-0 z-50"
    >
      <div className="lg:w-[69rem] mx-auto">
        <AtAnchorLinks links={links} accountForNavbar={accountForNavbar} onClick={onClick} visibleKey={visibleKey} />
      </div>
      {button && <AtButton {...button} />}
    </div>
  )
}
